import * as feedApi from '@apis/app-feed';
import * as productApi from '@apis/app-products';
import ProductItem from '@components/app/common/ProductItem';
import ShareButton from '@components/app/common/ShareButton';
import { ProductStatus } from '@consts/products';
import { useAppNavigation } from '@hooks/appNavigation';
import { DeepLinkActions, useDeeplink } from '@hooks/deeplink';
import { ProductModel } from '@models/product';
import { useQuery } from '@tanstack/react-query';
import { shuffleArray } from '@utils/dataTransform';
import { cdnImageUrl } from '@utils/image';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import * as AppLayout from '../AppLayout';
import { Helmet } from 'react-helmet-async';
import { useAuthentication } from '@hooks/authentication';
import { useAppUserSessionStore } from '@stores/appUserSessionStore';
import { ProductCollectionSearchItem } from '@models/product-collection';
import { getCurrentLocation, getDistanceBetweenGpsCoordinates } from '@utils/common';
import { eventManager } from '@stores/eventManager';
import ClipLoader from 'react-spinners/ClipLoader';

export const CollectionMainPage: React.FC = () => {
  const sessionStore = useAppUserSessionStore();
  const { myMembership } = useAuthentication();
  const [products, setProducts] = useState<ProductModel[]>([]);
  const [searchParams] = useSearchParams();
  const appNavigation = useAppNavigation();
  const scrollbarRef = useRef<any>();
  const scrollTopRef = useRef<number>();
  const { linkTo } = useDeeplink();
  const location = useLocation();
  const params = useParams();
  const collectionId = location.state?.collectionId || params.collectionId || searchParams.get('collectionId');

  const collectionItemQuery = useQuery({
    queryKey: [`collection-item-${collectionId}`],
    queryFn: () => feedApi.getCollectionItem(collectionId),
    enabled: !!collectionId,
  });

  const data = collectionItemQuery.data?.data;
  const title = data?.title || '기획전';
  const selectedSearchItem =
    sessionStore.getSessionCollectionSearchItem(collectionId) || ({} as ProductCollectionSearchItem);
  const searchItemCoordinates = selectedSearchItem?.coordinates?.split(',').map((point) => Number(point));
  const currentLocation = searchItemCoordinates
    ? { longitude: searchItemCoordinates[0], latitude: searchItemCoordinates[1] }
    : sessionStore.currentLocation;
  const productSearchQuery = useQuery({
    queryKey: [
      `product-collection-search-products`,
      JSON.stringify(selectedSearchItem),
      JSON.stringify(currentLocation || {}),
    ],
    queryFn: () =>
      productApi.searchProducts({
        status: 'open',
        page: 0,
        coordinates:
          selectedSearchItem.type === 'coordinates'
            ? selectedSearchItem.coordinates
            : currentLocation?.longitude
              ? `${currentLocation?.longitude},${currentLocation?.latitude}`
              : undefined,
        size: selectedSearchItem.size || 100,
        saleType: selectedSearchItem.saleType,
        keyword: selectedSearchItem.type === 'keyword' ? selectedSearchItem.kewyrod : undefined,
        sort: selectedSearchItem.sort,
      }),
    enabled: !!(selectedSearchItem.kewyrod || selectedSearchItem.coordinates),
  });

  useEffect(() => {
    eventManager.addEventListener('navigation_back', 'navigation_back_collection_page', () => {
      sessionStore.setSessionCollectionSearchItem(collectionId, {} as ProductCollectionSearchItem);
    });
    getCurrentLocation().then((currentLocation) => {
      sessionStore.setCurrentLocation(currentLocation);
    });
    return () => {
      eventManager.removeEventListener('navigation_back', 'navigation_back_collection_page');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let collectionProducts = data?.products || [];
    if (data?.shuffle) {
      const savedCollections = sessionStore.getSessionCollection(collectionId);
      if (savedCollections) {
        collectionProducts = savedCollections;
      } else {
        collectionProducts = shuffleArray(data?.products || []);
        sessionStore.setSessionCollection(collectionId, collectionProducts);
      }
    }
    setProducts(collectionProducts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.products]);

  useEffect(() => {
    if (data?.searchItems) {
      if (!selectedSearchItem.id) {
        sessionStore.setSessionCollectionSearchItem(collectionId, data.searchItems[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.searchItems]);

  useEffect(() => {
    if (productSearchQuery.data?.data.contents) {
      setProducts(productSearchQuery.data?.data.contents);
    }
  }, [productSearchQuery.data?.data.contents]);

  useEffect(() => {
    document.title = data?.title ? `아이고고 | ${data.title}` : `아이고고 | ${title}`;

    return () => {
      document.title = '아이고고 - 아이를 위한 모든 경험';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.title]);

  const onClickItem = (item: ProductModel) => {
    appNavigation.update({
      path: location.pathname,
      state: {
        collectionId: collectionId,
        scrollTop: scrollbarRef.current?.scrollTop || 0,
      },
    });
    linkTo(
      {
        action: DeepLinkActions.GO_TO_PRODUCT,
        payload: { productId: item._id },
      },
      { source: 'collection', collectionId: collectionId },
    );
  };
  return (
    <AppLayout.Layout>
      <Helmet>
        <title>{`아이고고 | ${data?.title}`}</title>
        <meta name="description" content={(data?.products || []).map((product) => product.title).join(' / ')} />
      </Helmet>
      <AppLayout.Header
        title={title}
        rightButton={
          <ShareButton
            id="collection-main-nav-right"
            iconColor={'#242424'}
            shareOptions={{ title: data?.title, id: collectionId, type: 'collection' }}
          />
        }></AppLayout.Header>
      <AppLayout.Contents>
        <div
          ref={(ref) => {
            scrollbarRef.current = ref;

            if (scrollbarRef.current) {
              scrollbarRef.current.scrollTop = scrollTopRef.current || location.state?.scrollTop || 0;
            }
          }}
          onScroll={() => {
            scrollTopRef.current = scrollbarRef.current?.scrollTop;
          }}
          style={{
            backgroundColor: '#ffffff',
            marginTop: 0,
            position: 'relative',
            height: '100%',
            overflow: 'auto',
            width: '100%',
          }}>
          <div
            style={{
              justifyContent: 'center',
              position: 'relative',
              alignItems: 'center',
              flex: 1,
            }}>
            {data?.coverImageUrl && (
              <img
                style={{
                  width: '100%',
                }}
                src={cdnImageUrl(data?.coverImageUrl)}
                alt=""
              />
            )}
          </div>
          {data?.searchItems && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', padding: '12px 8px 0px 8px' }}>
              {data?.searchItems.map((item: ProductCollectionSearchItem) => {
                const selected = selectedSearchItem?.id === item.id;
                return (
                  <button
                    key={item.id}
                    className="link-button"
                    style={{ height: '32px' }}
                    onClick={() => {
                      sessionStore.setSessionCollectionSearchItem(collectionId, item);
                    }}>
                    <div
                      style={{
                        display: 'flex',
                        height: '32px',
                        borderRadius: '16px',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        fontSize: '14px',
                        border: selected ? '1px solid #424242' : '1px solid #e8eaed',
                        color: selected ? '#ffffff' : '#424242',
                        backgroundColor: selected ? '#242424' : '#ffffff',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}>
                      <div>{item.label}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {(collectionItemQuery.isLoading || productSearchQuery.isLoading) && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
              <ClipLoader color="#424242" size={24} />
            </div>
          )}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              gap: '24px 12px',
              padding: '16px',
            }}>
            {products.map((item, index) => {
              const exposed =
                item.display && (item.status === ProductStatus.OPEN || item.status === ProductStatus.CLOSE);
              if (exposed) {
                const distance =
                  currentLocation?.latitude &&
                  currentLocation?.longitude &&
                  item.gpsLocation?.coordinates &&
                  getDistanceBetweenGpsCoordinates(
                    item.gpsLocation?.coordinates[1],
                    item.gpsLocation?.coordinates[0],
                    currentLocation?.latitude,
                    currentLocation?.longitude,
                  );

                return (
                  <div style={{ flex: '0 0 auto', width: 'calc(50% - 6px)' }}>
                    <ProductItem
                      key={`product-item-${index}`}
                      item={item}
                      distance={distance}
                      membership={myMembership}
                      onClick={() => {
                        onClickItem(item);
                      }}></ProductItem>
                  </div>
                );
              }
              return <></>;
            })}
          </div>
        </div>
      </AppLayout.Contents>
      <AppLayout.Footer hide></AppLayout.Footer>
    </AppLayout.Layout>
  );
};

export default CollectionMainPage;
